import { chainCommands, deleteSelection, newlineInCode, liftEmptyBlock, baseKeymap } from 'prosemirror-commands';
import { CommonExtension } from '../../../core/index.js';
import { Fragment } from 'prosemirror-model';
import { TextSelection, NodeSelection, Selection } from 'prosemirror-state';
import { liftTarget, canSplit, canJoin } from 'prosemirror-transform';
import { v4 } from 'uuid';
import { GapCursor, gapCursorSituation } from '../../../citadel/extensions/gap-cursor-extension/index.js';
import 'prosemirror-inputrules';

var convertCommand = function(commandFunction) {
    return function(param) {
        var state = param.state, dispatch = param.dispatch, view = param.view, args = param.args;
        return commandFunction(state, dispatch, view, args);
    };
};

var _typeof = function(obj) {
    "@swc/helpers - typeof";
    return obj && typeof Symbol !== "undefined" && obj.constructor === Symbol ? "symbol" : typeof obj;
};
var isOfType = function(type) {
    return function(value) {
        return (typeof value === "undefined" ? "undefined" : _typeof(value)) === type;
    };
};
var isString = isOfType("string");
var isPlainObject = function(obj) {
    if (typeof obj !== "object" || obj === null) return false;
    var proto = obj;
    while(Object.getPrototypeOf(proto) !== null){
        proto = Object.getPrototypeOf(proto);
    }
    return Object.getPrototypeOf(obj) === proto;
};
var omit = function(target, omitKey) {
    var filterKey = isString(omitKey) ? [
        omitKey
    ] : omitKey;
    if (isPlainObject(target)) {
        return Object.keys(target).filter(function(key) {
            return !filterKey.includes(key);
        }).reduce(function(prev, cur) {
            prev[cur] = target[cur];
            return prev;
        }, Object.create(null));
    }
    return target.filter(function(p) {
        return !filterKey.includes(p);
    });
};

/**
 * TODO: 节点类型是支持gapcursor的，由于目前部分gapcursor定义存在问题，在不保证修改schema的情况下做一层胶水
 * 后续如果schema统一治理，需要将这段代码考虑优化
 */ var allowGapCursor = function(node) {
    if (!node) {
        return false;
    }
    var allow = node.type.spec.allowGapCursor;
    if (allow) {
        return true;
    }
    var textType = [
        "catalog"
    ];
    return textType.includes(node.type.name);
};

function _arrayLikeToArray(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _arrayWithoutHoles(arr) {
    if (Array.isArray(arr)) return _arrayLikeToArray(arr);
}
function _instanceof(left, right) {
    if (right != null && typeof Symbol !== "undefined" && right[Symbol.hasInstance]) {
        return !!right[Symbol.hasInstance](left);
    } else {
        return left instanceof right;
    }
}
function _iterableToArray(iter) {
    if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}
function _nonIterableSpread() {
    throw new TypeError("Invalid attempt to spread non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _toConsumableArray(arr) {
    return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread();
}
function _unsupportedIterableToArray(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _arrayLikeToArray(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}
var defaultBlockAt = function(match) {
    for(var i = 0; i < match.edgeCount; i++){
        var type = match.edge(i).type;
        if (type.isTextblock && !type.hasRequiredAttrs()) return type;
    }
    return null;
};
var splitBlock = function(state, dispatch) {
    var selection = state.selection, schema = state.schema;
    var $from = selection.$from, $to = selection.$to, from = selection.from;
    var paragraph = schema.nodes.paragraph;
    if (_instanceof(state.selection, NodeSelection) && state.selection.node.isBlock) {
        if (!$from.parentOffset || !canSplit(state.doc, $from.pos)) return false;
        if (dispatch) dispatch(state.tr.split($from.pos).scrollIntoView());
        return true;
    }
    if (!$from.parent.isBlock) return false;
    if (dispatch) {
        var atStart = $to.parentOffset === 0;
        var atEnd = $to.parentOffset === $to.parent.content.size;
        var tr = state.tr;
        if (_instanceof(state.selection, TextSelection)) tr.deleteSelection();
        var parentNode = $from.node(-1);
        var deflt = $from.depth === 0 ? null : defaultBlockAt($from.node(-1).contentMatchAt($from.indexAfter(-1)));
        var types = atEnd && deflt ? [
            {
                type: deflt
            }
        ] : null;
        var splitDepth = 1;
        if (deflt === schema.nodes.text) {
            if (!$from.nodeBefore || !$to.nodeAfter) {
                tr.insert(from, parentNode.type.createAndFill());
                tr.setSelection(TextSelection.create(tr.doc, from + 2));
                tr.delete(from + 2, from + 4);
                dispatch(tr.scrollIntoView());
                return true;
            }
            splitDepth = 2;
        }
        var can = canSplit(tr.doc, tr.mapping.map($from.pos), 1, types);
        if (!types && !can && canSplit(tr.doc, tr.mapping.map($from.pos), splitDepth, deflt && [
            {
                type: deflt
            }
        ])) {
            types = [
                {
                    type: deflt
                }
            ];
            can = true;
        }
        if (can) {
            if (atStart) {
                tr.insert(tr.mapping.map($from.pos) - 1, paragraph.createAndFill());
            } else {
                tr.split(tr.mapping.map($from.pos), splitDepth, types || [
                    {
                        type: deflt,
                        attrs: {
                            dataDiffId: "ct-diff-id-".concat(v4())
                        }
                    }
                ]);
                if (!atEnd && !$from.parentOffset && $from.parent.type !== deflt && $from.node(-1).canReplace($from.index(-1), $from.indexAfter(-1), Fragment.from([
                    deflt.create(),
                    $from.parent
                ]))) {
                    tr.setNodeMarkup(tr.mapping.map($from.before()), deflt);
                }
            }
        }
        dispatch(tr.scrollIntoView());
    }
    return true;
};
var createParagraphNear = function(state, dispatch) {
    var selection = state.selection;
    var $from = selection.$from, $to = selection.$to;
    if ($from.parent.inlineContent || $to.parent.inlineContent) {
        return false;
    }
    var type = defaultBlockAt($from.parent.contentMatchAt($to.indexAfter()));
    if (!type || !type.isTextblock) {
        return false;
    }
    if (dispatch) {
        // TODO: gapCursor situation
        var side = (!$from.parentOffset && $to.index() < $to.parent.childCount ? $from : $to).pos;
        var tr = state.tr.insert(side, type.createAndFill());
        tr.setSelection(TextSelection.create(tr.doc, side + 1));
        dispatch(tr.scrollIntoView());
    }
    return true;
};
var splitBlockKeepMarks = function(baseKeyMapProvider) {
    return function(state, dispatch, view) {
        return splitBlock(state, dispatch && function(tr) {
            var marks = state.storedMarks || state.selection.$to.parentOffset && state.selection.$from.marks();
            if (marks) {
                if (baseKeyMapProvider) {
                    // 下面的处理是为了支持通过配置去除切分块级元素时不需要保留的Mark，例如划词评论的引用
                    var newMarks = _toConsumableArray(marks);
                    newMarks.forEach(function(mark, index) {
                        var _baseKeyMapProvider_excludeMarkNames, _baseKeyMapProvider_excludeMarkNames1;
                        if (baseKeyMapProvider.excludeMarkNames && ((_baseKeyMapProvider_excludeMarkNames = baseKeyMapProvider.excludeMarkNames) === null || _baseKeyMapProvider_excludeMarkNames === void 0 ? void 0 : _baseKeyMapProvider_excludeMarkNames.length) > 0 && ((_baseKeyMapProvider_excludeMarkNames1 = baseKeyMapProvider.excludeMarkNames) === null || _baseKeyMapProvider_excludeMarkNames1 === void 0 ? void 0 : _baseKeyMapProvider_excludeMarkNames1.indexOf(mark.type.name)) !== -1) {
                            newMarks.splice(index, 1);
                        }
                        tr.ensureMarks(newMarks);
                    });
                } else {
                    tr.ensureMarks(marks);
                }
            }
            dispatch(tr);
        });
    };
};
var overwriteEnterKeymap = function(baseKeyMapProvider) {
    return convertCommand(chainCommands(newlineInCode, createParagraphNear, liftEmptyBlock, splitBlockKeepMarks(baseKeyMapProvider)));
};
var overwriteTabKeymap = convertCommand(function() {
    return true;
});
var overwriteShiftTabKeymap = convertCommand(function() {
    return true;
});
var overwriteShiftEnterKeymap = function(baseKeyMapProvider) {
    return convertCommand(function(state, dispatch, view) {
        var tr = state.tr, selection = state.selection, schema = state.schema;
        var $from = selection.$from;
        var hard_break = schema.nodes.hard_break;
        // TODO: gapCursor situation
        if ($from && $from.parent.type.spec.textContentBlock && hard_break) {
            tr.insert($from.pos, hard_break.createAndFill());
            dispatch(tr);
            return true;
        }
        return overwriteEnterKeymap(baseKeyMapProvider)({
            state: state,
            dispatch: dispatch,
            view: view
        });
    });
};
var deleteSelectedNode = function(state, dispatch, view) {
    var tr = state.tr, selection = state.selection;
    if (_instanceof(selection, NodeSelection)) {
        var node = selection.node;
        if (node.isInline) {
            tr.deleteSelection();
            var $pos = tr.doc.resolve(selection.from);
            var newSelection;
            if (GapCursor.valid($pos)) {
                var type = GapCursor.getType($pos);
                newSelection = new GapCursor($pos, type);
            } else {
                newSelection = TextSelection.create(tr.doc, $pos.pos);
            }
            // 内部会触发一次 setSelection，但它的结果不正确，我们不使用它
            tr.setSelection(newSelection);
        } else {
            tr.replaceSelectionWith(state.schema.nodes.paragraph.create());
            // 移动光标到新插入的段落节点内
            tr.setSelection(TextSelection.create(tr.doc, selection.from + 1));
        }
        dispatch(tr.scrollIntoView());
        view.focus();
        return true;
    }
    if (!selection.empty) {
        return false;
    }
    var nodeBefore = selection.$from.nodeBefore;
    if (_instanceof(selection, GapCursor)) {
        if (selection.gapCursorType === gapCursorSituation.beforeInlineNode) {
            if (nodeBefore && nodeBefore.type.name === "text") {
                dispatch(tr.delete(tr.selection.from - 1, tr.selection.from).setSelection(TextSelection.create(tr.doc, tr.selection.from)));
                return true;
            }
        }
    }
    return false;
};
var selectNodeBefore = function(state, dispatch, view) {
    var selection = state.selection, tr = state.tr;
    if (!selection.empty) {
        return false;
    }
    var nodeBefore = selection.$from.nodeBefore;
    if (_instanceof(selection, GapCursor)) {
        if (selection.gapCursorType === gapCursorSituation.afterNode || selection.gapCursorType === gapCursorSituation.afterInlineNode) {
            tr.setSelection(NodeSelection.create(tr.doc, tr.selection.from - nodeBefore.nodeSize));
            dispatch(tr);
            return true;
        }
    }
    return false;
};
// 找到一个行与第二行之间的断裂的$pos信息
function findCutBefore($pos) {
    if (!$pos.parent.type.spec.isolating) {
        for(var i = $pos.depth - 1; i >= 0; i--){
            if ($pos.index(i) > 0) return $pos.doc.resolve($pos.before(i + 1));
            if ($pos.node(i).type.spec.isolating) break;
        }
    }
    return null;
}
// 当光标处于一个节点的首部时, (在backspace的情况下)如果可能，将光标转移至上一个
// 节点的尾部，这里区分gapCursor及TextSelection两种情况:
// 前方节点的schema中有allowGapCursor则放置gapCursor，gapCursor区分inline-node及node两种
// 前方是普通文本则直接将光标放到上一个文本节点的尾部
function setSelectionBackward(state, dispatch, view) {
    var _state_selection = state.selection, $head = _state_selection.$head, empty = _state_selection.empty;
    var $cut = $head;
    if (!empty) return false;
    if ($head.parent.isTextblock) {
        if (view ? !view.endOfTextblock("backward", state) : $head.parentOffset > 0) {
            // 向左移动如果不离开当前的textBlock节点
            return false;
        }
        $cut = findCutBefore($head);
    }
    var node = $cut && $cut.nodeBefore;
    if (!node || !NodeSelection.isSelectable(node)) {
        return false;
    }
    if (dispatch) {
        if (allowGapCursor(node)) {
            if (GapCursor.valid($cut)) {
                var type = GapCursor.getType($cut);
                if (type === gapCursorSituation.afterInlineNode || type === gapCursorSituation.afterNode) {
                    view.dispatch(view.state.tr.setSelection(new GapCursor($cut, type)));
                    return true;
                }
            }
        } else {
            view.dispatch(view.state.tr.setSelection(TextSelection.create(view.state.tr.doc, $cut.pos - 1)));
            return true;
        }
    }
    return true;
}
// prosemirror-commands 1.1.7
function joinMaybeClear(state, $pos, dispatch) {
    var before = $pos.nodeBefore;
    var after = $pos.nodeAfter;
    var index = $pos.index();
    // 这里最好还是修改schema - https://km.sankuai.com/page/841048610
    if (!before || !after || !(before.type.compatibleContent(after.type) && ![
        "spaceupdate",
        "page_tree"
    ].includes(before.type.name))) {
        if (before.type.name === "blockquote" && after) {
            var tr = state.tr;
            tr.insert($pos.pos - 1, after);
            tr.delete(tr.mapping.map($pos.pos), tr.mapping.map($pos.pos) + after.nodeSize);
            tr.setSelection(TextSelection.create(tr.doc, $pos.pos - 1));
            dispatch(tr);
            return true;
        }
        return false;
    }
    if (!before.content.size && $pos.parent.canReplace(index - 1, index)) {
        if (dispatch) {
            dispatch(state.tr.delete($pos.pos - before.nodeSize, $pos.pos).scrollIntoView());
        }
        return true;
    }
    if (!$pos.parent.canReplace(index, index + 1) || !(after.isTextblock || canJoin(state.doc, $pos.pos))) {
        return false;
    }
    if (dispatch) {
        var tr1 = state.tr;
        dispatch(tr1.clearIncompatible($pos.pos, before.type, before.contentMatchAt(before.childCount)).join($pos.pos).scrollIntoView().setSelection(TextSelection.near(tr1.doc.resolve($pos.pos - 1), -1)));
    }
    return true;
}
// prosemirror-commands 1.1.7
function deleteBarrier(state, $cut, dispatch) {
    var before = $cut.nodeBefore;
    var after = $cut.nodeAfter;
    if (before.type.spec.isolating || after.type.spec.isolating) return false;
    if (joinMaybeClear(state, $cut, dispatch)) {
        return true;
    }
    var selAfter = Selection.findFrom($cut, 1);
    var range = selAfter && selAfter.$from.blockRange(selAfter.$to);
    var target = range && liftTarget(range);
    if (target != null && target >= $cut.depth) {
        if (dispatch) dispatch(state.tr.lift(range, target).scrollIntoView());
        return true;
    }
    return false;
}
// 在 joinForward 时需要递归判断，如果下一行是表格，则光标出现在第一个单元格内
// 之前修复 ONES#10238531 时删除递归逻辑，现加上发现不会复现该 Bug
function textblockAt(node, side) {
    for(; node; node = side === "start" ? node.firstChild : node.lastChild){
        if (node.isTextblock) return true;
    }
    return false;
}
function joinBackward(state, dispatch, view) {
    var selection = state.selection;
    var $cursor = selection.$cursor, $from = selection.$from, gapCursorType = selection.gapCursorType;
    var $cut = null;
    if (gapCursorType === gapCursorSituation.beforeInlineNode) {
        $cut = findCutBefore($from);
    } else {
        if (!$cursor || (view ? !view.endOfTextblock("backward", state) : $cursor.parentOffset > 0)) {
            return false;
        }
        $cut = findCutBefore($cursor);
    }
    // If there is no node before this, try to lift
    if (!$cut) {
        // 解决 $cursor 不存在时的报错问题 #21134649
        if (!$cursor) {
            return false;
        }
        var range = $cursor.blockRange();
        var target = range && liftTarget(range);
        if (target == null) {
            return false;
        }
        if (dispatch) {
            dispatch(state.tr.lift(range, target).scrollIntoView());
        }
        return true;
    }
    var before = $cut.nodeBefore;
    // Apply the joining algorithm
    if (!before.type.spec.isolating && (!allowGapCursor(before) || before.type.name === "blockquote") && deleteBarrier(state, $cut, dispatch)) {
        return true;
    }
    // If the node below has no content and the node above is
    // selectable, delete the node below and select the one above.
    if ($cursor.parent.content.size === 0 && (textblockAt(before, "end") || NodeSelection.isSelectable(before))) {
        if (dispatch) {
            // 此处逻辑为包含 inline content 的时候才会执行选中节点操作
            // 在 schema 定义中目录树满足此条件，但是目录不满足，所以表现并不相同。
            // TODO: 考虑是否对所有 schema 的 content 属性进行清理
            if (textblockAt(before, "end") && !allowGapCursor(before)) {
                var tr = state.tr.deleteRange($cursor.before(), $cursor.after());
                tr.setSelection(Selection.findFrom(tr.doc.resolve(tr.mapping.map($cut.pos, -1)), -1));
                dispatch(tr.scrollIntoView());
                return true;
            }
            // 如果光标前面是 selectable 的节点，那么绘制 Gapcursor，并且处理删除当前空行，但是不对光标进行重新定位
            var tr1 = state.tr.deleteRange($cursor.before(), $cursor.after());
            if (allowGapCursor(before)) {
                var selectionType = GapCursor.getType($cut);
                if (GapCursor.valid($cut) && (selectionType === gapCursorSituation.afterInlineNode || selectionType === gapCursorSituation.afterNode)) {
                    tr1.setSelection(new GapCursor(tr1.doc.resolve($cut.pos), selectionType === gapCursorSituation.afterInlineNode ? undefined : selectionType));
                    dispatch(tr1.scrollIntoView());
                    return true;
                }
            }
        }
    }
    if (before.isAtom && $cut.depth === $cursor.depth - 1) {
        if (allowGapCursor(before)) {
            if (GapCursor.valid($cut)) {
                var type = GapCursor.getType($cut);
                if (type === gapCursorSituation.afterInlineNode || type === gapCursorSituation.afterNode) {
                    view.dispatch(view.state.tr.setSelection(new GapCursor($cut, type)));
                    return true;
                }
            }
        } else {
            // 非allowGapcursor本身又是atom的节点，直接选中，比如目录、最近更新
            view.dispatch(view.state.tr.setSelection(NodeSelection.create(view.state.tr.doc, $cut.pos - before.nodeSize)));
            return true;
        }
        return true;
    }
    return false;
}
function findCutAfter($pos) {
    if (!$pos.parent.type.spec.isolating) {
        for(var i = $pos.depth - 1; i >= 0; i--){
            var parent = $pos.node(i);
            if ($pos.index(i) + 1 < parent.childCount) {
                return $pos.doc.resolve($pos.after(i + 1));
            }
            if (parent.type.spec.isolating) break;
        }
    }
    return null;
}
// 如果在第一行空行按删除，则删除该行，光标在下一行的开头
function deleteEmptyLine(state, dispatch, view) {
    var $cursor = state.selection.$cursor;
    if ($cursor && $cursor.parent.isTextblock && $cursor.parent.childCount === 0 && // 该节点的前一个节点不能被删除
    !$cursor.parent.type.spec.disableDeleteBefore) {
        var $cut = findCutAfter($cursor);
        if (!$cut || !$cut.nodeAfter) return false;
        var after = $cut.nodeAfter;
        if (textblockAt(after, "start") || NodeSelection.isSelectable(after)) {
            var tr = state.tr.deleteRange($cursor.before(), $cursor.after());
            tr.setSelection(textblockAt(after, "start") ? Selection.findFrom(tr.doc.resolve(tr.mapping.map($cut.pos)), 1) : NodeSelection.create(tr.doc, tr.mapping.map($cut.pos)));
            dispatch(tr.scrollIntoView());
            return true;
        }
    }
    return false;
}
var overwriteBackspace = convertCommand(chainCommands(selectNodeBefore, deleteSelectedNode, deleteSelection, joinBackward, setSelectionBackward, deleteEmptyLine));

function _arrayLikeToArray$1(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _arrayWithHoles(arr) {
    if (Array.isArray(arr)) return arr;
}
function _assertThisInitialized(self) {
    if (self === void 0) {
        throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
    }
    return self;
}
function _classCallCheck(instance, Constructor) {
    if (!(instance instanceof Constructor)) {
        throw new TypeError("Cannot call a class as a function");
    }
}
function _defineProperties(target, props) {
    for(var i = 0; i < props.length; i++){
        var descriptor = props[i];
        descriptor.enumerable = descriptor.enumerable || false;
        descriptor.configurable = true;
        if ("value" in descriptor) descriptor.writable = true;
        Object.defineProperty(target, descriptor.key, descriptor);
    }
}
function _createClass(Constructor, protoProps, staticProps) {
    if (protoProps) _defineProperties(Constructor.prototype, protoProps);
    if (staticProps) _defineProperties(Constructor, staticProps);
    return Constructor;
}
function _defineProperty(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _getPrototypeOf(o) {
    _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) {
        return o.__proto__ || Object.getPrototypeOf(o);
    };
    return _getPrototypeOf(o);
}
function _inherits(subClass, superClass) {
    if (typeof superClass !== "function" && superClass !== null) {
        throw new TypeError("Super expression must either be null or a function");
    }
    subClass.prototype = Object.create(superClass && superClass.prototype, {
        constructor: {
            value: subClass,
            writable: true,
            configurable: true
        }
    });
    if (superClass) _setPrototypeOf(subClass, superClass);
}
function _iterableToArrayLimit(arr, i) {
    var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
    if (_i == null) return;
    var _arr = [];
    var _n = true;
    var _d = false;
    var _s, _e;
    try {
        for(_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true){
            _arr.push(_s.value);
            if (i && _arr.length === i) break;
        }
    } catch (err) {
        _d = true;
        _e = err;
    } finally{
        try {
            if (!_n && _i["return"] != null) _i["return"]();
        } finally{
            if (_d) throw _e;
        }
    }
    return _arr;
}
function _nonIterableRest() {
    throw new TypeError("Invalid attempt to destructure non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _objectSpread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty(target, key, source[key]);
        });
    }
    return target;
}
function _possibleConstructorReturn(self, call) {
    if (call && (_typeof$1(call) === "object" || typeof call === "function")) {
        return call;
    }
    return _assertThisInitialized(self);
}
function _setPrototypeOf(o, p) {
    _setPrototypeOf = Object.setPrototypeOf || function _setPrototypeOf(o, p) {
        o.__proto__ = p;
        return o;
    };
    return _setPrototypeOf(o, p);
}
function _slicedToArray(arr, i) {
    return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray$1(arr, i) || _nonIterableRest();
}
var _typeof$1 = function(obj) {
    "@swc/helpers - typeof";
    return obj && typeof Symbol !== "undefined" && obj.constructor === Symbol ? "symbol" : typeof obj;
};
function _unsupportedIterableToArray$1(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _arrayLikeToArray$1(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$1(o, minLen);
}
function _isNativeReflectConstruct() {
    if (typeof Reflect === "undefined" || !Reflect.construct) return false;
    if (Reflect.construct.sham) return false;
    if (typeof Proxy === "function") return true;
    try {
        Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function() {}));
        return true;
    } catch (e) {
        return false;
    }
}
function _createSuper(Derived) {
    var hasNativeReflectConstruct = _isNativeReflectConstruct();
    return function _createSuperInternal() {
        var Super = _getPrototypeOf(Derived), result;
        if (hasNativeReflectConstruct) {
            var NewTarget = _getPrototypeOf(this).constructor;
            result = Reflect.construct(Super, arguments, NewTarget);
        } else {
            result = Super.apply(this, arguments);
        }
        return _possibleConstructorReturn(this, result);
    };
}
var BaseKeyMapExtension = /*#__PURE__*/ function(CommonExtension) {
    _inherits(BaseKeyMapExtension, CommonExtension);
    var _super = _createSuper(BaseKeyMapExtension);
    function BaseKeyMapExtension() {
        _classCallCheck(this, BaseKeyMapExtension);
        return _super.apply(this, arguments);
    }
    var _proto = BaseKeyMapExtension.prototype;
    _proto.keys = function keys(param) {
        var providers = param.providers;
        var baseKeyMapProvider = providers.get(this.name);
        var overwriteRules = {
            Enter: overwriteEnterKeymap(baseKeyMapProvider),
            "Shift-Enter": overwriteShiftEnterKeymap(baseKeyMapProvider),
            Backspace: overwriteBackspace,
            "Shift-Backspace": overwriteBackspace,
            Tab: overwriteTabKeymap,
            "Shift-Tab": overwriteShiftTabKeymap
        };
        var mappedKeys = _objectSpread({}, Object.entries(omit(baseKeymap, "Enter")).reduce(function(prev, param) {
            var _param = _slicedToArray(param, 2), key = _param[0], value = _param[1];
            prev[key] = convertCommand(value);
            return prev;
        }, {}), overwriteRules);
        return mappedKeys;
    };
    _createClass(BaseKeyMapExtension, [
        {
            key: "name",
            get: function get() {
                return "base_key_map";
            }
        }
    ]);
    return BaseKeyMapExtension;
}(CommonExtension);

function _assertThisInitialized$1(self) {
    if (self === void 0) {
        throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
    }
    return self;
}
function _classCallCheck$1(instance, Constructor) {
    if (!(instance instanceof Constructor)) {
        throw new TypeError("Cannot call a class as a function");
    }
}
function _defineProperty$1(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _get(target, property, receiver) {
    if (typeof Reflect !== "undefined" && Reflect.get) {
        _get = Reflect.get;
    } else {
        _get = function _get(target, property, receiver) {
            var base = _superPropBase(target, property);
            if (!base) return;
            var desc = Object.getOwnPropertyDescriptor(base, property);
            if (desc.get) {
                return desc.get.call(receiver);
            }
            return desc.value;
        };
    }
    return _get(target, property, receiver || target);
}
function _getPrototypeOf$1(o) {
    _getPrototypeOf$1 = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) {
        return o.__proto__ || Object.getPrototypeOf(o);
    };
    return _getPrototypeOf$1(o);
}
function _inherits$1(subClass, superClass) {
    if (typeof superClass !== "function" && superClass !== null) {
        throw new TypeError("Super expression must either be null or a function");
    }
    subClass.prototype = Object.create(superClass && superClass.prototype, {
        constructor: {
            value: subClass,
            writable: true,
            configurable: true
        }
    });
    if (superClass) _setPrototypeOf$1(subClass, superClass);
}
function _objectSpread$1(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty$1(target, key, source[key]);
        });
    }
    return target;
}
function _possibleConstructorReturn$1(self, call) {
    if (call && (_typeof$2(call) === "object" || typeof call === "function")) {
        return call;
    }
    return _assertThisInitialized$1(self);
}
function _setPrototypeOf$1(o, p) {
    _setPrototypeOf$1 = Object.setPrototypeOf || function _setPrototypeOf(o, p) {
        o.__proto__ = p;
        return o;
    };
    return _setPrototypeOf$1(o, p);
}
function _superPropBase(object, property) {
    while(!Object.prototype.hasOwnProperty.call(object, property)){
        object = _getPrototypeOf$1(object);
        if (object === null) break;
    }
    return object;
}
var _typeof$2 = function(obj) {
    "@swc/helpers - typeof";
    return obj && typeof Symbol !== "undefined" && obj.constructor === Symbol ? "symbol" : typeof obj;
};
function _isNativeReflectConstruct$1() {
    if (typeof Reflect === "undefined" || !Reflect.construct) return false;
    if (Reflect.construct.sham) return false;
    if (typeof Proxy === "function") return true;
    try {
        Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function() {}));
        return true;
    } catch (e) {
        return false;
    }
}
function _createSuper$1(Derived) {
    var hasNativeReflectConstruct = _isNativeReflectConstruct$1();
    return function _createSuperInternal() {
        var Super = _getPrototypeOf$1(Derived), result;
        if (hasNativeReflectConstruct) {
            var NewTarget = _getPrototypeOf$1(this).constructor;
            result = Reflect.construct(Super, arguments, NewTarget);
        } else {
            result = Super.apply(this, arguments);
        }
        return _possibleConstructorReturn$1(this, result);
    };
}
var BaseKeyMapPCExtension = /*#__PURE__*/ function(BaseKeyMapExtension) {
    _inherits$1(BaseKeyMapPCExtension, BaseKeyMapExtension);
    var _super = _createSuper$1(BaseKeyMapPCExtension);
    function BaseKeyMapPCExtension() {
        _classCallCheck$1(this, BaseKeyMapPCExtension);
        return _super.apply(this, arguments);
    }
    var _proto = BaseKeyMapPCExtension.prototype;
    _proto.keys = function keys(props) {
        return _objectSpread$1({}, _get(_getPrototypeOf$1(BaseKeyMapPCExtension.prototype), "keys", this).call(this, props));
    };
    return BaseKeyMapPCExtension;
}(BaseKeyMapExtension);

export { BaseKeyMapPCExtension };
//# sourceMappingURL=index.js.map
